import { Check, Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { extractErrorMessage } from '@/services/errors/errors';
import { HTTPWrapper } from '@/utils/http';

const generationEnvFileSchema = z.object({
  filename: z.string(),
  content: z.string()
});

const generationEnvPreviewSchema = z.object({
  env: generationEnvFileSchema,
  repositories: generationEnvFileSchema
});

type GenerationEnvFile = z.infer<typeof generationEnvFileSchema>;
type GenerationEnvPreview = z.infer<typeof generationEnvPreviewSchema>;

type GenerationEnvDialogProps = {
  processId: string;
  workspaceId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/** Icon button that copies `value` to the clipboard and flips to a check briefly. */
const CopyButton = ({ value, label }: { value: string; label: string }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    []
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`Copied ${label}`);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setCopied(false);
        timerRef.current = null;
      }, 2000);
    } catch (error) {
      toast.error(`Failed to copy: ${extractErrorMessage(error)}`);
    }
  };

  return (
    <Button
      aria-label={`Copy ${label}`}
      className="h-6 w-6 p-0"
      onClick={handleCopy}
      size="sm"
      type="button"
      variant="ghost"
    >
      {copied ? (
        <Check className="h-3 w-3 text-green-600" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </Button>
  );
};

/** One file section: filename header with a copy button, then the content or skeleton. */
const FileSection = ({
  file,
  loading
}: {
  file: GenerationEnvFile | null;
  loading: boolean;
}) => (
  <div className="overflow-hidden rounded-lg border border-border">
    <div className="flex items-center justify-between gap-2 border-border border-b bg-muted/40 px-3 py-2">
      {loading || !file ? (
        <Skeleton className="h-4 w-40" />
      ) : (
        <>
          <code className="truncate font-mono text-sm">{file.filename}</code>
          <CopyButton label={file.filename} value={file.content} />
        </>
      )}
    </div>
    <pre className="max-h-64 overflow-auto bg-card p-3 text-muted-foreground text-xs">
      {loading || !file ? (
        <div className="space-y-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
          <Skeleton className="h-3 w-3/5" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ) : (
        <code>{file.content}</code>
      )}
    </pre>
  </div>
);

/**
 * Privileged tool: previews the two files needed to reproduce a generation
 * locally — the `.env.<stage>` file (which references the repositories payload
 * via REPOSITORIES_FILE_PATH) and the `repositories.<stage>.json` file. Both
 * are fetched from the backend and can be copied independently.
 */
export const GenerationEnvDialog = ({
  processId,
  workspaceId,
  open,
  onOpenChange
}: GenerationEnvDialogProps) => {
  const [data, setData] = useState<GenerationEnvPreview | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    setData(null);
    setLoading(true);

    const wsParam = workspaceId ? `?workspaceId=${workspaceId}` : '';
    const http = new HTTPWrapper('process');

    http
      .get<unknown>(`/${processId}/generation-env${wsParam}`)
      .then(raw => {
        if (cancelled) {
          return;
        }
        setData(generationEnvPreviewSchema.parse(raw));
      })
      .catch(error => {
        if (cancelled) {
          return;
        }
        toast.error(`Failed to load env: ${extractErrorMessage(error)}`);
        onOpenChange(false);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, processId, workspaceId, onOpenChange]);

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generation environment</DialogTitle>
          <DialogDescription>
            Copy each file to its named location to reproduce this generation
            locally.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="gap-4">
          <FileSection file={data?.env ?? null} loading={loading} />
          <FileSection file={data?.repositories ?? null} loading={loading} />
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};
